import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupData, Service } from 'webapp-types';
import { GoalTargetValueType, TimeUnit } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import GoalsPopupCompareOtherMetricComponent from 'webapp/components/goals/popup-compare-other-metric';
import GoalsTargetTypeAndGranulationListItemComponent from 'webapp/components/goals/target-type-and-granulation/list-item';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

export interface SwitchOption {
	label: string;
	value: GoalTargetValueType;
}

export interface ShownOption extends LabelValuePairNumeric {
	isSeparated?: boolean;
	tooltipText?: string;
}

export const BY_END_DATE_TOOLTIP_TEXT =
	'The goal’s target is applied to the end date of the selected timeframe. If you need a different end date, you can override it with a custom due date in advanced period settings.';
const OtherMetricValue = 99;
export default class GoalsTargetTypeAndGranulationPopupComponent extends PopupInnerContextComponent<PopupData.Goal.TargetTypeAndGranulation> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('popup') declare popupService: Service.Popup;

	@tracked targetValueType: GoalTargetValueType = this.popupData.targetValueType;
	@tracked selectedPrimaryGranulation: number = this.popupData.targetOtherMetric
		? OtherMetricValue
		: this.primaryGranulation;

	get primaryGranulation() {
		return this.popupData.primaryGranulation;
	}

	get isAutomaticGoal() {
		return this.popupData.isAutomaticGoal;
	}

	get showOtherMetric() {
		return !this.popupData.hideOtherMetric && this.isAutomaticGoal;
	}

	get switchOptions(): SwitchOption[] {
		return [
			{
				label: 'Fixed value',
				value: GoalTargetValueType.FIXED,
			},
			{
				label: '% change',
				value: GoalTargetValueType.PERCENTAGE,
			},
		];
	}

	get shownOptions(): ShownOption[] {
		const options: ShownOption[] = [];
		const isFixedTargetValue = this.targetValueType === GoalTargetValueType.FIXED;

		[TimeUnit.DAY, TimeUnit.WEEK, TimeUnit.MONTH, TimeUnit.QUARTER, TimeUnit.YEAR].forEach(
			(timeUnit) => {
				if (!isFixedTargetValue && timeUnit === TimeUnit.DAY) {
					return;
				}

				options.push({
					value: timeUnit,
					label: this.mmGoalsService.getGranulationItemString(
						isFixedTargetValue,
						timeUnit,
					),
				});
			},
		);

		if (isFixedTargetValue) {
			if (this.popupData.allowByEndDate) {
				options.push({
					value: TimeUnit.TOTAL,
					label: 'By end date',
					tooltipText: BY_END_DATE_TOOLTIP_TEXT,
				});
			}

			if (this.showOtherMetric) {
				options.push({
					value: OtherMetricValue,
					label: 'Other metric',
					isSeparated: true,
				});
			}
		}

		return options;
	}

	@action
	changeSelectedTargetValueType(value: AnySimpleValue) {
		const targetValueType = value as GoalTargetValueType;
		let primaryGranulation = this.primaryGranulation;

		if (
			(targetValueType === GoalTargetValueType.PERCENTAGE &&
				primaryGranulation === TimeUnit.DAY) ||
			primaryGranulation === TimeUnit.TOTAL
		) {
			primaryGranulation = TimeUnit.MONTH;
		}

		this.selectedPrimaryGranulation = primaryGranulation;
		this.targetValueType = targetValueType;
		this.popupData.onChange(this.targetValueType, primaryGranulation);
	}

	@action
	changeSelectedOption(selectedOption: ShownOption) {
		// compare with other metric selected
		if (selectedOption.value === OtherMetricValue) {
			const popup = this.popupService.createModal(GoalsPopupCompareOtherMetricComponent, {
				allowStacked: true,
			});
			popup.open({
				targetOtherMetric: this.popupData.targetOtherMetric,
				onChange: this.onOtherMetricChange,
			});
		} else {
			this.selectedPrimaryGranulation = selectedOption.value;
			this.popupData.onChange(this.targetValueType, selectedOption.value);
		}
		this.close();
	}

	@action
	onOtherMetricChange(metricSetupData: DSSetup.MetricSetupData) {
		this.popupData.onChange(this.targetValueType, this.primaryGranulation, metricSetupData);
		this.selectedPrimaryGranulation = OtherMetricValue;
	}
	<template>
		<ul class="links">
			{{#if this.isAutomaticGoal}}
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@name="type"
					@selected={{this.targetValueType}}
					@options={{this.switchOptions}}
					@onChange={{this.changeSelectedTargetValueType}}
				/>
			{{/if}}
			{{#each this.shownOptions as |item|}}
				<GoalsTargetTypeAndGranulationListItemComponent
					@model={{item}}
					@isSelected={{eq this.selectedPrimaryGranulation item.value}}
					@onSelect={{this.changeSelectedOption}}
				/>
			{{/each}}
		</ul>
	</template>
}
